// "We don't support this file yet": when a trade file doesn't import, the trader can send it to
// us in one click (platformRequest.js on the server), or email it with a message already written.
import { useState } from 'react';
import { api } from './api';
import { useI18n } from './i18n';

const MAX_BYTES = 3 * 1024 * 1024;
const HELLO = 'hello@marketmatrixhq.com';

const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

export default function PlatformRequest({ file, user, from = 'import' }) {
  const { t } = useI18n();
  const member = user && !user.isDemo;
  const [platform, setPlatform] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [agree, setAgree] = useState(false);
  const [state, setState] = useState('idle'); // idle | sending | sent
  const [error, setError] = useState('');
  if (!file) return null;
  const tooBig = file.size > MAX_BYTES;

  const subject = `Please add my platform${platform ? `: ${platform}` : ''}`;
  const body = [
    'Hi Market Matrix,',
    '',
    `My trade file doesn't import yet. Could you add support for ${platform || 'my platform'}?`,
    `I've attached the file I tried (${file.name}).`,
    '',
    'Thanks!',
  ].join('\n');
  const mailto = `mailto:${HELLO}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  async function send(e) {
    e.preventDefault();
    setState('sending');
    setError('');
    try {
      const data = await toBase64(file);
      await api('/platform-request', { method: 'POST', body: { fileName: file.name, fileType: file.type, data, platform, note, email, from } });
      setState('sent');
    } catch (err) {
      setError(err.message);
      setState('idle');
    }
  }

  if (state === 'sent') {
    return (
      <div className="platform-request sent" role="status">
        <strong>{t('Thanks, we got your file.')}</strong>
        <p>{t('We’ll add your platform and email you when it’s ready, usually within a few days.')}</p>
      </div>
    );
  }

  return (
    <form className="platform-request" onSubmit={send}>
      <strong className="platform-request-title">{t('We don’t support this file yet, but adding your platform is a quick fix.')}</strong>
      <p>{t('Send us this file and we’ll add your platform, usually within a few days. We’ll email you when it’s ready.')}</p>
      {tooBig ? (
        <p className="error">{t('This file is over 3 MB, too big to send here. Email it to us instead:')} <a href={mailto}>{HELLO}</a></p>
      ) : (
        <>
          <div className="platform-request-fields">
            <label>{t('Platform or broker (optional)')}<input value={platform} onChange={(e) => setPlatform(e.target.value)} placeholder={t('e.g. Rithmic, Interactive Brokers, MT5')} maxLength={80} /></label>
            {!member && <label>{t('Your email')}<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>}
          </div>
          <label>{t('Anything we should know? (optional)')}<input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder={t('e.g. it’s the Account Statement from the website')} /></label>
          <label className="reminder-check platform-request-agree">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} required />
            {t('I’m OK sharing {file} with Market Matrix so you can add support for it. It’s only used to build the import, then deleted.', { file: file.name })}
          </label>
          {error && <p className="error">{error}</p>}
          <div className="platform-request-actions">
            <button type="submit" className="small" disabled={!agree || state === 'sending'}>{state === 'sending' ? t('Sending…') : t('Send it to Market Matrix')}</button>
            <span className="platform-request-or">{t('or')} <a href={mailto}>{t('email it to us')}</a> {t('(attach the file yourself)')}</span>
          </div>
        </>
      )}
    </form>
  );
}
